import moment from 'moment/moment'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiBadge, EuiButton, EuiLink, EuiSwitch, EuiText, EuiToolTip, SortDirection } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { MasterCampaignCampaignItem } from 'api/interfaces/MasterCampaign'
import { useDeleteMasterCampaignCampaignMutation, useGetMasterCampaignCampaignsQuery, useGetMasterCampaignQuery, useUpdateMasterCampaignMutation } from 'api/rtkQueryApi/platform/masterCampaignsApi'
import { RootState } from 'app/rootReducer'
import AgencyV2ModalOld from 'features/planner/old-pages/components/AgencyV2ModalOld'

import { Campaign, CampaignStatus } from '../../../api/interfaces'
import { AcEuiInMemoryTableWithSearch } from '../../../components/Basic/AcEuiInMemoryTableWithSearch'
import history from '../../../services/HistoryService'
import { CampaignStatusToElasticColor, CampaignStatusToString } from '../../../utils/EnumToFriendly'
import useDebounceCallback from '../../../utils/useDebounceCallback'
import { setCampaign } from '../campaign/campaignSlice'

export type MasterCampaignTableItem = MasterCampaignCampaignItem & {
  campaign: Campaign
}

interface GroupCampaignsListProps {
  masterCampaignId: string
}

export const GroupCampaignsList = ({ masterCampaignId }: GroupCampaignsListProps) => {
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [masterCampaignItems, setMasterCampaignItems] = useState<MasterCampaignTableItem[]>([])

  const dispatch = useDispatch()
  const [updateMasterCampaign] = useUpdateMasterCampaignMutation()
  const campaignsQuery = useGetMasterCampaignCampaignsQuery({ masterCampaignId: masterCampaignId }, { skip: !masterCampaignId })
  const masterCampaignQuery = useGetMasterCampaignQuery({ masterCampaignId: masterCampaignId }, { skip: !masterCampaignId })
  const [deleteMasterCampaignCampaignMutation, deleteMasterCampaignCampaignResult] = useDeleteMasterCampaignCampaignMutation()
  const isDirtyRef = React.useRef(false)

  const [selectedCampaignForDeleteModalOpen, setSelectedCampaignForDeleteModalOpen] = useState<Campaign | undefined>()

  useEffect(() => {
    if (!masterCampaignQuery.data || !campaignsQuery.data || isDirtyRef.current) {
      return
    }
    const masterCampaign = masterCampaignQuery.data
    const campaigns = campaignsQuery.data
    const groupItems: MasterCampaignTableItem[] = masterCampaign.items
      .map(item => {
        const campaign = campaigns.find(c => c.id === item.campaignId)
        if (!campaign) {
          return undefined
        }
        return {
          ...item,
          campaign
        }
      })
      .filter(item => item !== undefined) as MasterCampaignTableItem[]

    setMasterCampaignItems(groupItems)
  }, [campaignsQuery, masterCampaignQuery])

  const updateItems = useDebounceCallback(1000, (newMasterCampaignItems: MasterCampaignCampaignItem[]) => {
    if (newMasterCampaignItems) {
      updateMasterCampaign({
        masterCampaignId: masterCampaignId,
        data: {
          name: masterCampaignQuery.data?.name,
          paymentMethodId: masterCampaignQuery.data?.paymentMethodId,
          activateDaysBeforeStartDate: masterCampaignQuery.data?.activateDaysBeforeStartDate,
          updatedBy: '',
          masterCampaignItems: newMasterCampaignItems.map(item => ({
            campaignId: item.campaignId,
            autoActivate: item.autoActivate,
            paymentMethodId: item.paymentMethodId,
            activateDaysBeforeStartDate: item.activateDaysBeforeStartDate
          }))
        }
      }).finally(() => {
        isDirtyRef.current = false
      })
    }
  })

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  const onCampaignClick = (campaign: Campaign) => {
    dispatch(setCampaign(campaign))
    history.push(`/campaigns/edit/${campaign.id}/details`)
  }

  const switchDisabled = (campaign: Campaign): boolean => {
    const startDate = moment(campaign.startDate)
    const today = moment()
    const daysBeforeStartDate = startDate.diff(today, 'days')
    return daysBeforeStartDate <= 0 || campaign.status !== CampaignStatus.SavedForLater || masterCampaignQuery.isLoading || campaignsQuery.isLoading
  }

  const tooltipContent = (item: MasterCampaignTableItem) => {
    const startDate = moment(item.campaign.startDate)
    const today = moment()
    const daysBeforeStartDate = startDate.diff(today, 'days')

    if (switchDisabled(item.campaign)) {
      if (daysBeforeStartDate <= 0 && !item.autoActivate) {
        return 'Cannot activate once it is on or past the start date.'
      }
      return 'Cannot disable once the campaign is activated'
    }
  }

  const onAutoActivateChange = (item: MasterCampaignTableItem) => {
    isDirtyRef.current = true
    updateItems(
      masterCampaignItems.map(i => ({
        ...i,
        autoActivate: i.campaignId === item.campaignId ? !i.autoActivate : i.autoActivate
      }))
    )

    setMasterCampaignItems(items => {
      const itemToUpdate = items.find(i => i.campaignId === item.campaignId)
      if (!itemToUpdate) {
        return items
      }

      itemToUpdate.autoActivate = !itemToUpdate.autoActivate
      return [...items]
    })
  }

  const onDeleteMasterCampaignCampaign = async () => {
    if (!currentAccount?.id || !selectedCampaignForDeleteModalOpen) {
      return
    }
    await deleteMasterCampaignCampaignMutation({ accountId: currentAccount.id, masterCampaignId: masterCampaignId, campaignId: selectedCampaignForDeleteModalOpen.id })
    setSelectedCampaignForDeleteModalOpen(undefined)
  }

  const columns: EuiBasicTableColumn<MasterCampaignTableItem>[] = [
    {
      name: 'Auto Activate',
      field: 'autoActivate',
      render: (autoActivate: boolean, item: MasterCampaignTableItem) => {
        return (
          <EuiToolTip content={tooltipContent(item)}>
            <EuiSwitch
              label=''
              checked={autoActivate}
              onChange={() => {
                onAutoActivateChange(item)
              }}
              disabled={switchDisabled(item.campaign)}
            />
          </EuiToolTip>
        )
      },
      sortable: (item: MasterCampaignTableItem) => item.autoActivate
    },
    {
      name: 'Campaign',
      field: 'campaign.name',
      render: (name: string, item: MasterCampaignTableItem) => {
        return (
          <EuiLink onClick={() => onCampaignClick(item.campaign)} color='success'>
            {name}
          </EuiLink>
        )
      },
      sortable: (item: MasterCampaignTableItem) => item.campaign.name,
      width: '25%'
    },
    {
      name: `Type`,
      render: (item: MasterCampaignTableItem) => {
        return <div className='campaignType'>{item.campaign.type}</div>
      },
      sortable: (item: MasterCampaignTableItem) => item.campaign.type
    },
    {
      name: `Start Date`,
      render: (item: MasterCampaignTableItem) => {
        return moment(item.campaign.startDate).format('MM/DD/yyyy')
      },
      sortable: (item: MasterCampaignTableItem) => moment(item.campaign.startDate).valueOf()
    },
    {
      name: `Submission Date`,
      render: (item: MasterCampaignTableItem) => {
        const daysBeforeStartDate = item.activateDaysBeforeStartDate ? item.activateDaysBeforeStartDate : masterCampaignQuery.data?.activateDaysBeforeStartDate
        return moment(item.campaign.startDate).subtract(daysBeforeStartDate, 'days').format('MM/DD/yyyy')
      },
      sortable: (item: MasterCampaignTableItem) => {
        const daysBeforeStartDate = item.activateDaysBeforeStartDate ? item.activateDaysBeforeStartDate : masterCampaignQuery.data?.activateDaysBeforeStartDate
        return moment(item.campaign.startDate).subtract(daysBeforeStartDate, 'days').valueOf()
      }
    },
    {
      name: `Monthly Budget`,
      render: (item: MasterCampaignTableItem) => {
        return formatter.format(item.campaign.budget) + (item.campaign.budgetFrequency.toLowerCase() === 'total' ? ' total' : ``)
      },
      sortable: (item: MasterCampaignTableItem) => item.campaign.budget
    },
    {
      name: `Number of Months`,
      render: (item: MasterCampaignTableItem) => {
        if (item.campaign.flightSchedule?.isIndefinite) {
          return 'Indefinite'
        }
        if (item.campaign.budgetFrequency.toLowerCase() === 'total') {
          return '-'
        }
        return item.campaign.flightSchedule?.totalIntervals
      },
      sortable: (item: MasterCampaignTableItem) => item.campaign.budget
    },
    {
      name: `Status`,
      render: (item: MasterCampaignTableItem) => {
        return <EuiBadge color={CampaignStatusToElasticColor(item.campaign.status)}>{item.campaign.status === CampaignStatus.SavedForLater && item.autoActivate ? 'Upcoming' : CampaignStatusToString(item.campaign)}</EuiBadge>
      },
      sortable: (item: MasterCampaignTableItem) => item.campaign.status
    },
    {
      name: 'Actions',
      actions: [
        {
          name: 'Delete',
          description: 'Delete this campaign',
          icon: 'trash',
          type: 'icon',
          color: 'danger',
          onClick: (item: MasterCampaignTableItem) => {
            setSelectedCampaignForDeleteModalOpen(item.campaign)
          },
          enabled: (item: MasterCampaignTableItem) => item.campaign.status === CampaignStatus.SavedForLater,
          available: () => !deleteMasterCampaignCampaignResult.isLoading
        }
      ]
    }
  ]

  const sorting = {
    allowNeutralSort: true,
    enableAllColumns: false,
    sort: {
      field: 'Start Date',
      direction: SortDirection.ASC
    }
  }

  const isLoading = masterCampaignQuery.isLoading || campaignsQuery.isLoading

  return (
    <>
      <AcEuiInMemoryTableWithSearch
        items={masterCampaignItems}
        columns={columns}
        message={isLoading ? 'Loading campaigns...' : 'No campaigns'}
        loading={isLoading}
        sorting={sorting}
        searchFields={['status', 'campaign.name', 'type']}
        search={{
          box: {
            incremental: true
          },
          toolsRight: [
            <EuiButton
              fill
              size={'s'}
              onClick={() => {
                history.push(`/build?masterCampaignId=${masterCampaignId}`)
              }}>
              Create Campaign
            </EuiButton>
          ]
        }}
      />
      {selectedCampaignForDeleteModalOpen && (
        <AgencyV2ModalOld isLoading={deleteMasterCampaignCampaignResult.isLoading} title='Delete Campaign' onClose={() => setSelectedCampaignForDeleteModalOpen(undefined)} confirmButtonText='Delete' onConfirmButtonClick={onDeleteMasterCampaignCampaign} showCancelButton>
          <EuiText>Are you sure you want to delete this campaign?</EuiText>
        </AgencyV2ModalOld>
      )}
    </>
  )
}
